import { IBorderRadiusData, IFontData, ImageRatioType, IWidgetVisibility, SelfPosition, VisibilityData, WidgetPlaceType } from "./index";
import { IElementWithLink } from "./link-data";
import { IAnimatedWidget } from "./widget-style-data";

export interface IGalleryWidgetData extends IWidgetVisibility {
    readonly type: "GalleryWidget";
    placeType: WidgetPlaceType;
    id?: string | null;
    isNeedTitle: boolean;
    isNeedDescription: boolean;
    imgRatio?: ImageRatioType;
    infoPosition: InfoPosition;
    hoverAnimationType?: HoverAnimationType;
    style: IGalleryWidgetStyle;
    images: IGalleryImageData[];
    actionType: GalleryClickActionType;
}

export interface IGalleryImageData extends IElementWithLink {
    id?: string | null;
    src?: string | null;
    altText?: string | null;
    title?: string | null;
    fileName?: string | null;
    description?: string | null;
}

export interface IGalleryWidgetStyle extends IAnimatedWidget {
    displayShadow?: boolean | null;
    shadowColor?: string;
    borderRadius: IBorderRadiusData;
    horizontalAlignment: SelfPosition;
    verticalAlignment: SelfPosition;
    fontTitle?: IFontData | null;
    fontDescription?: IFontData | null;
    spaceBetweenImages: number;
    imageInRow: number;
    pagination: IGalleryWidgetPagination;
    overlayColor?: string;
    magnifyingGlassConfig: IMagnifyingGlassConfig;
}

export interface IMagnifyingGlassConfig {
    enabled: boolean;
    zoomFactor: number;
}

export interface IGalleryWidgetPagination {
    alignment: SelfPosition;
    visibleRows: number;
    isLoadMoreButtonVisible: boolean;
    loadMoreButtonColor?: string;
}

export interface IGalleryWidgetDataShort {
    isNeedTitle: boolean;
    isNeedDescription: boolean;
    images: IGalleryImageData[];
    imgRatio?: ImageRatioType;
    imageBorder: IBorderRadiusData;
    infoPosition: InfoPosition;
    actionType: GalleryClickActionType;
    style: IGalleryWidgetStyle;
    hoverAnimationType?: HoverAnimationType;
}

export enum InfoPosition {
    Underneath = "underneath",
    Above = "above",
    OnImage = "onImage" //required for galleries in saved templates
}

export enum GalleryClickActionType {
    None = "none",
    OpenLink = "openLink",
    OpenFullScreenMode = "openFullScreenMode"
}

export type HoverAnimationType = "no-change" | "appears" | "disappears";

export abstract class GalleryWidgetData {

    public static localScriptRerenderNeeded(leftHandSide?: IGalleryWidgetData, rightHandSide?: IGalleryWidgetData): boolean {
        return leftHandSide?.id === rightHandSide?.id &&
            leftHandSide?.infoPosition === rightHandSide?.infoPosition &&
            leftHandSide?.imgRatio === rightHandSide?.imgRatio &&
            leftHandSide?.isNeedTitle === rightHandSide?.isNeedTitle &&
            leftHandSide?.isNeedDescription === rightHandSide?.isNeedDescription &&
            leftHandSide?.style.borderRadius === rightHandSide?.style.borderRadius &&
            leftHandSide?.images === rightHandSide?.images &&
            leftHandSide?.style.magnifyingGlassConfig.enabled === rightHandSide?.style.magnifyingGlassConfig.enabled &&
            leftHandSide?.style.magnifyingGlassConfig.zoomFactor === rightHandSide?.style.magnifyingGlassConfig.zoomFactor;
    }

    public static get defaultData(): IGalleryWidgetData {
        return {
            type: "GalleryWidget",
            placeType: "Section",
            isNeedTitle: true,
            isNeedDescription: true,
            imgRatio: "ratio-normal",
            infoPosition: InfoPosition.Underneath,
            hoverAnimationType: "no-change",
            style: this.defaultStyle,
            visibility: VisibilityData.defaultData(),
            images: [],
            actionType: GalleryClickActionType.OpenFullScreenMode
        };
    }

    public static get defaultStyle(): IGalleryWidgetStyle {
        return {
            displayShadow: false,
            shadowColor: "rgba(0, 0, 0, 0.5)",
            borderRadius: {
                topLeftPx: 0,
                topRightPx: 0,
                bottomLeftPx: 0,
                bottomRightPx: 0
            },
            spaceBetweenImages: 12,
            horizontalAlignment: "center",
            verticalAlignment: "center",
            fontTitle: this.defaultFont,
            fontDescription: this.defaultFont,
            imageInRow: 6,
            pagination: {
                alignment: "center",
                visibleRows: 3,
                isLoadMoreButtonVisible: false,
                loadMoreButtonColor: "#3B507D"
            },
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                }
            },
            overlayColor: "rgba(0, 0, 0, 0.4)",
            magnifyingGlassConfig: {
                enabled: false,
                zoomFactor: 200
            }
        };
    }

    private static get defaultFont(): IFontData {
        return {
            weight: "",
            family: "",
            size: "",
            lineHeight: "1.5em"
        };
    }

}
